import { motion } from "framer-motion";
import { ArrowRight, Quote } from "lucide-react";
import { ABOUT_IMG, ABOUT_THUMBS } from "@/constants/aero";
import { SOCIALS } from "@/constants/content";

const ease = [0.16, 1, 0.3, 1];

export const AeroAbout = () => (
  <section id="about" data-testid="aero-about" className="bg-[#FAF9F6] py-20 md:py-28 border-y border-ink/5">
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
      {/* Left — bio */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease }}
        className="lg:col-span-4"
      >
        <p className="font-mono text-[14px] tracking-[0.3em] uppercase text-goldtext">My Story</p>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl text-ink leading-[1.08]">
          From Passion<br />to Perspective
        </h2>
        <p className="mt-6 text-neutral-600 leading-relaxed text-[15px] md:text-base">
          From a curious engineer to a Bollywood aerial cinematographer, my journey has been about turning real places into cinematic
          stories. I have had the privilege of working on feature films, OTT series, and national projects, capturing India from
          perspectives few get to see.
        </p>
        <a
          data-testid="aero-about-story"
          href={SOCIALS.instagram}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center gap-3 border border-ink/25 text-ink font-mono text-[13px] font-bold tracking-[0.18em] uppercase px-7 py-3.5 rounded-full hover:border-goldtext hover:text-goldtext transition-colors"
        >
          Read My Full Story <ArrowRight size={13} />
        </a>
        <p className="mt-8 font-[Caveat] text-3xl text-ink/70 -rotate-2 select-none">Akhilesh Kumar Suman</p>
      </motion.div>

      {/* Center — B&W photo */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease }}
        className="lg:col-span-4 relative"
      >
        <div className="absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 border-goldtext/60 rounded-tl-2xl" />
        <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b-2 border-r-2 border-goldtext/60 rounded-br-2xl" />
        <img
          src={ABOUT_IMG}
          alt="Akhilesh Kumar Suman — black and white portrait"
          loading="lazy"
          data-testid="aero-about-photo"
          className="w-full aspect-[4/5] object-cover object-center rounded-2xl shadow-[0_30px_80px_-30px_rgba(10,10,10,0.4)]"
        />
      </motion.div>

      {/* Right — pull quote */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay: 0.1, ease }}
        className="lg:col-span-3"
      >
        <Quote size={34} className="text-goldtext/50" fill="currentColor" strokeWidth={0} />
        <p className="mt-4 font-serif italic text-3xl md:text-4xl text-ink leading-[1.2]">
          The sky isn't my limit.<br />It's my canvas.
        </p>
        <span className="mt-6 block w-12 h-px bg-goldtext/60" />
      </motion.div>

      {/* Far right — aerial thumbnails */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, delay: 0.15, ease }}
        className="lg:col-span-1 flex lg:flex-col gap-3"
        data-testid="aero-about-thumbs"
      >
        {ABOUT_THUMBS.map((t) => (
          <img
            key={t.src}
            src={t.src}
            alt={t.alt}
            loading="lazy"
            className="w-20 h-20 lg:w-full lg:h-auto lg:aspect-square object-cover rounded-lg border border-ink/10 hover:border-goldtext/60 hover:scale-[1.03] transition-all duration-300"
          />
        ))}
      </motion.div>
    </div>
  </section>
);
